import { ZKEX_WS_URL } from 'config'
import { SymbolName } from '../../types'
import { EventEmitter } from './emitter'
import { getAccountWsToken } from 'api/v1/account'

export interface SubscribeOrderbookPayload {
  symbol: SymbolName
}
export interface UnsubscribeOrderbookPayload {
  symbol: SymbolName
}

export type SubscribePayload =
  | SubscribeOrderbookPayload
  | UnsubscribeOrderbookPayload

export interface ActionSubscribeData extends SubscribeOrderbookPayload {
  action: 'subscribe'
}
export interface ActionUnsubscribeData extends UnsubscribeOrderbookPayload {
  action: 'unsubscribe'
}
export interface ActionPingData {
  action: 'ping'
}

export type ActionData =
  | ActionSubscribeData
  | ActionUnsubscribeData
  | ActionPingData

export class ZkexConnection {
  ws?: WebSocket
  protected connectId: number = 0
  protected userToken?: string

  private readyEmitter = new EventEmitter<ZkexConnection>()

  async connect(userToken?: string): Promise<ZkexConnection> {
    let url = ZKEX_WS_URL
    this.userToken = userToken
    if (userToken) {
      const token = await this.getWsToken(userToken)
      if (token) {
        url += `?token=${token}`
      } else {
        return this
      }
    }
    return this.createConnection(url)
  }

  async getWsToken(userToken: string): Promise<string> {
    if (!userToken) {
      throw new Error('User token is not exists')
    }
    const { result } = await getAccountWsToken()
      .then((r) => r.data)
      .catch((e) => {
        console.error(e)
        return { result: '' }
      })

    if (userToken !== this.userToken) {
      console.warn('User token is changed')
      return ''
    }
    return result
  }

  createConnection(url: string): ZkexConnection {
    // Try to disconnect the previous connection
    this.disconnect()

    this.ws = new WebSocket(url)
    this.connectId += 1

    this.onClose((event) => {
      // 1000: CLOSE_NORMAL
      // Don't reconnect when the connection is closed normally
      if (event.code === 1000) {
        console.debug('Zkex connection closed')
      } else if (event.code === 1006) {
        console.debug('Zkex connection was replaced')
      } else {
        console.error('Zkex connection closed abnormally, retry in 1s.')
        setTimeout(() => {
          this.connect(this.userToken)
        }, 1000)
      }
    })

    this.onOpen(() => {
      this.readyEmitter.publish('ready', this)
    })

    return this
  }

  getConnectId() {
    return this.connectId
  }

  isReady() {
    if (!this.ws) {
      return false
    }
    return this.ws.readyState === WebSocket.OPEN
  }

  onReady(callback: (data: ZkexConnection) => void) {
    this.readyEmitter.subscribe('ready', callback)
  }

  // 1000: CLOSE_NORMAL
  disconnect(code: number = 1000) {
    console.debug('ZkexConnection.disconnect')
    this.ws?.close(code)
  }

  on<K extends keyof WebSocketEventMap>(
    event: K,
    listener: (this: WebSocket, ev: WebSocketEventMap[K]) => any,
  ) {
    this.ws!.addEventListener(event, listener)

    return () => {
      this.ws!.removeEventListener(event, listener)
    }
  }

  onMessage<T>(callback: (data: T, event: MessageEvent) => void) {
    return this.on('message', (event: MessageEvent) => {
      callback(JSON.parse(event.data), event)
    })
  }

  onOpen(callback: (connection: ZkexConnection) => void) {
    return this.on('open', () => {
      callback(this)
    })
  }

  onClose(callback: (event: CloseEvent) => void) {
    return this.on('close', (event: CloseEvent) => {
      callback(event)
    })
  }

  onError(callback: (event: Event) => void) {
    return this.on('error', (event: Event) => {
      callback(event)
    })
  }

  subscribe(payload: SubscribePayload) {
    const data: ActionSubscribeData = {
      action: 'subscribe',
      ...payload,
    }
    this.send(data as ActionSubscribeData)

    return () => {
      this.unsubscribe(payload)
    }
  }

  unsubscribe(payload: SubscribePayload) {
    this.send({
      action: 'unsubscribe',
      ...payload,
    })
  }

  send(data: ActionData | string) {
    if (!this.isReady()) {
      console.debug('ZkexConnection.send: ws is not connected', data)
      return
    }
    let sendData: string = ''
    if (typeof data !== 'string') {
      sendData = JSON.stringify(data)
    }
    this.ws!.send(sendData)
  }

  ping() {
    this.send({
      action: 'ping',
    })
  }
}
